/**
 * 回放通道：把 `.tlog` 变成**另一种传输通道**。
 *
 * 这是整个回放功能里最重要的一个决定。`ReplayTransport` 实现的就是 `@hgc/link` 的
 * `Transport` 契约（`kind` / `label` / `state` / `open` / `close` / `write` / `subscribe`），
 * 于是对上层（会话、车辆状态机、参数与任务管理器、界面）来说，回放与真机**没有任何区别**：
 * 同一个 `MavlinkSession`、同一套帧解析、同一套心跳与在线判定、同一套界面绑定，
 * 一行都不用为回放改写。
 *
 * 换成"回放专用的一条数据通路"看似更直接，代价却是致命的：回放里看到的行为与真机不一致，
 * 而回放存在的全部意义就是复盘真机发生了什么 —— 只要两条路径不完全相同，复盘结论就不可信；
 * 而且以后每改一次链路层或界面，都要记得改两遍。
 *
 * 关于 `write()`：回放时没有对端接收写入。这里**忽略写入但如实计数**，而不是抛错。
 * 抛错看起来更"正确"，但会话层的自动行为（心跳、指令重试、频率协商）在回放时照常发生，
 * 一个 reject 就会把界面变成满屏错误 —— 那全是噪声，还会盖住真正要看的遥测。
 * 计数则保留了"回放期间有多少写入被丢弃"这个事实，可以直接显示在链路诊断里。
 */
import { BaseTransport, type TransportKind, type TransportMetrics } from '@hgc/link';

import {
  TlogPlayer,
  systemReplayClock,
  type ReplayClock,
  type ReplayProgress,
  type ReplaySpeed,
  type TlogRecord,
  type TlogIncomplete,
  type TlogStats,
  TlogReader,
  type TlogReaderOptions,
} from './replay';

export interface ReplayTransportOptions {
  /** 给人看的标识，例如 `flight-2026-09-25.tlog`。默认 `回放` */
  readonly label?: string;
  readonly clock?: ReplayClock;
  readonly speed?: ReplaySpeed;
  /** 打开后立刻开始回放，默认 true */
  readonly autoplay?: boolean;
  /**
   * 传入的记录是不是"已经全部到齐"。
   * 默认：构造时带了记录就算到齐，构造时为空则视为流式（之后用 `append()` + `seal()`）。
   */
  readonly sealed?: boolean;
  readonly onFrame?: (frame: Uint8Array, record: TlogRecord) => void;
  /**
   * 播放 / 暂停 / 跳转 / 结束时回调。
   * **不是每帧都回调** —— 十万条记录就是十万次界面更新，进度条会把回放本身拖垮；
   * 进度条请按自己的节奏读 `progress()`。
   */
  readonly onProgress?: (progress: ReplayProgress) => void;
  /** 回放结束时给用户看的说明，默认 `回放已结束` */
  readonly endDetail?: string;
}

/** 回放期间被丢弃的写入统计 */
export interface ReplayWriteStats {
  readonly ignoredWrites: number;
  readonly ignoredBytes: number;
}

export class ReplayTransport extends BaseTransport {
  readonly kind: TransportKind = 'replay';
  readonly label: string;

  private readonly player: TlogPlayer;
  private readonly autoplay: boolean;
  private readonly frameListener: ((frame: Uint8Array, record: TlogRecord) => void) | undefined;
  private readonly progressListener: ((progress: ReplayProgress) => void) | undefined;
  private readonly endDetail: string;

  private ignoredWrites = 0;
  private ignoredBytes = 0;

  constructor(records: readonly TlogRecord[] = [], options: ReplayTransportOptions = {}) {
    super();
    this.label = options.label ?? '回放';
    this.autoplay = options.autoplay ?? true;
    this.frameListener = options.onFrame;
    this.progressListener = options.onProgress;
    this.endDetail = options.endDetail ?? '回放已结束';

    this.player = new TlogPlayer(records, {
      clock: options.clock ?? systemReplayClock,
      speed: options.speed ?? 1,
      ...(options.sealed === undefined ? {} : { sealed: options.sealed }),
      onFrame: (frame, record) => {
        // 出口只有一个：`emitData` 是 Transport 契约里"链路上来了字节"的唯一表达，
        // 会话从这里进来的字节流与从串口进来的完全同构
        this.frameListener?.(frame, record);
        this.emitData(frame);
      },
      onEnd: () => {
        this.setState('closed', this.endDetail);
        this.progressListener?.(this.player.progress());
      },
    });
  }

  /** 底层播放器。回放特有的控制（变速、跳转）也可以通过它操作。 */
  get playback(): TlogPlayer {
    return this.player;
  }

  get recordCount(): number {
    return this.player.total;
  }

  get isPlaying(): boolean {
    return this.player.isPlaying;
  }

  get speed(): ReplaySpeed {
    return this.player.speed;
  }

  progress(): ReplayProgress {
    return this.player.progress();
  }

  writes(): ReplayWriteStats {
    return { ignoredWrites: this.ignoredWrites, ignoredBytes: this.ignoredBytes };
  }

  /**
   * 回放没有链路质量可言：没有往返、没有信号强度。
   *
   * 返回 `null` 而不是 `{ latencyMs: 0, rssi: 0 }` —— 编造一个数字会让链路面板显示
   * "延迟 0ms"，那是在骗人；`null` 会让它显示"不适用"。
   */
  linkMetrics(): TransportMetrics | null {
    return null;
  }

  override async open(): Promise<void> {
    if (this.currentState === 'open') return;
    this.setState('opening');
    // 重新打开就等于"从头再看一遍"：不复用上一次的播放位置与播放状态
    this.player.rewind();
    this.setState('open');

    if (this.player.total === 0 && this.player.sealed) {
      this.setState('closed', '这份记录里没有任何帧');
      return;
    }
    if (this.autoplay) this.play();
    else this.progressListener?.(this.player.progress());
  }

  override async close(): Promise<void> {
    this.player.pause();
    this.setState('closed');
  }

  /** 忽略写入但如实计数：回放里没有对端（见文件头注释里的取舍） */
  override async write(bytes: Uint8Array): Promise<void> {
    this.ignoredWrites += 1;
    this.ignoredBytes += bytes.length;
  }

  play(): void {
    // 放完之后状态是 closed（会话据此显示"回放已结束"），再按播放要把它拉回 open
    if (this.currentState !== 'open') this.setState('open');
    this.player.play();
    this.progressListener?.(this.player.progress());
  }

  pause(): void {
    this.player.pause();
    this.progressListener?.(this.player.progress());
  }

  setSpeed(speed: ReplaySpeed): void {
    this.player.setSpeed(speed);
    this.progressListener?.(this.player.progress());
  }

  seek(index: number): void {
    this.player.seek(index);
    this.progressListener?.(this.player.progress());
  }

  seekToTimestamp(timestampUs: number): void {
    this.player.seekToTimestamp(timestampUs);
    this.progressListener?.(this.player.progress());
  }

  /** 流式回放：把新解出的记录喂进来 */
  append(records: readonly TlogRecord[]): void {
    this.player.append(records);
  }

  /** 声明不会再有记录了 */
  seal(): void {
    this.player.seal();
  }
}

export interface ReplayFromBytesOptions {
  readonly reader?: TlogReaderOptions;
  readonly transport?: ReplayTransportOptions;
}

export interface ReplayFromBytesResult {
  readonly transport: ReplayTransport;
  readonly stats: TlogStats;
  readonly incomplete: TlogIncomplete | null;
}

/**
 * 一次性把整份 `.tlog` 字节变成可以交给会话的回放通道。
 *
 * 大文件不要用它：那会把整份记录读进内存。界面上的"打开记录文件"走流式路径
 * （`TlogReader.push` → `transport.append`），这里只是给测试与短记录留的直路。
 */
export function createReplayTransportFromBytes(
  bytes: Uint8Array,
  options: ReplayFromBytesOptions = {},
): ReplayFromBytesResult {
  const reader = new TlogReader(options.reader ?? {});
  const records = reader.push(bytes);
  const incomplete = reader.end();
  const transport = new ReplayTransport(records, {
    ...(options.transport ?? {}),
    sealed: true,
  });
  return { transport, stats: reader.stats, incomplete };
}
